import type { IFixitAPIClient } from '@ifixit/ifixit-api-client';

/**
 * An API client for a page script. It sends the `Api-Client` header that the
 * MooTools API class sent, which the Varnish API stats count requests by.
 * Make one per request: IFixitAPIClient.activeRequests is keyed by client, so
 * a shared one drops a request from WebDriver's waitForRequest() while
 * another is still running.
 *
 * The client loads on first use. Imported up front it added 17.6 KiB to every
 * device page, for a workbench button most visits never click.
 */
export async function webApiClient(): Promise<IFixitAPIClient> {
   const { IFixitAPIClient: Client } = await import('@ifixit/ifixit-api-client');
   return new Client({
      origin: window.location.origin,
      headers: { 'Api-Client': 'iFixit-Web' },
   });
}

export function jsonBody(data: unknown): RequestInit {
   return {
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
   };
}

/**
 * For a request the server may spend minutes on. The client otherwise gives
 * up after 10 seconds.
 * This is setTimeout's largest delay; a bigger one fires at once.
 */
export const NO_TIMEOUT = { retry: { globalTimeout: 2_147_483_647 } };

/**
 * The reason the server refused the request (a 4xx with a message), or null
 * when it failed some other way (a dropped connection, a timeout, a 500),
 * whose message is not for the user to read.
 */
export function refusalMessage(error: unknown): string | null {
   const response = (error as { response?: unknown } | null)?.response;
   if (response instanceof Response && response.status < 500 && error instanceof Error) {
      return error.message || null;
   }
   return null;
}

/**
 * A React Query `retry` for reads. React Query retries a failed read 3 times
 * by default. A refusal (any 4xx, with or without a message) answers the same
 * every time, so only a dropped connection, a timeout or a 5xx is worth
 * another try.
 */
export function retryUnlessRefused(failureCount: number, error: unknown): boolean {
   const response = (error as { response?: unknown } | null)?.response;
   const refused = response instanceof Response && response.status < 500;
   return failureCount < 3 && !refused;
}
